import { useState, useEffect } from "react";
import { Modal, Form, Select, InputNumber, Switch, Button, Row, Col, Typography, message } from "antd";
import { get_available_ai_providers } from "../../api/admin.apis";
import PromptFields from "./PromptFields";
import { buildConfigPayload, hasStoredPrompt } from "./aiConfigPayloads";

const { Option } = Select;
const { Text } = Typography;

const formatLabel = (name) =>
  name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "-";

const EditAiConfigModal = ({ visible, loading, config, onSubmit, onClose }) => {
  const [form] = Form.useForm();
  const [providers, setProviders] = useState({});
  const [providersLoading, setProvidersLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [currentModelVersion, setCurrentModelVersion] = useState(null);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;
    setProvidersLoading(true);

    get_available_ai_providers()
      .then((res) => {
        if (cancelled) return;
        setProviders(res?.data?.data?.providers || {});
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Failed to load AI providers");
      })
      .finally(() => {
        if (cancelled) return;
        setProvidersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (visible && config) {
      setSelectedProvider(config.provider || null);
      setCurrentModelVersion(config.model_version || null);
      form.setFieldsValue({
        provider: config.provider,
        model_version: config.model_version,
        temperature: config.temperature ?? 0,
        max_tokens: config.max_tokens ?? 0,
        top_p: config.top_p ?? 0,
        frequency_penalty: config.frequency_penalty ?? 0,
        presence_penalty: config.presence_penalty ?? 0,
        enabled: config.enabled ?? true,
        system_prompt: config.system_prompt ?? "",
        user_prompt: config.user_prompt ?? "",
      });
    }
  }, [visible, config, form]);

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    setCurrentModelVersion(null);
    form.setFieldsValue({ model_version: undefined });
  };

  const handleFinish = (values) => {
    // We always render with the LLM in this admin panel; the deterministic
    // Python renderer is not exposed. Normalise the row on every save so an
    // older `true` value (set elsewhere) gets flipped back to the LLM path.
    onSubmit(buildConfigPayload(values));
  };

  const handleAfterClose = () => {
    form.resetFields();
    setSelectedProvider(null);
    setCurrentModelVersion(null);
  };

  const providerEntries = Object.entries(providers);
  const providerInfo = selectedProvider ? providers[selectedProvider] : null;
  const fetchedModels = providerInfo?.models || [];
  const modelOptions =
    currentModelVersion &&
    selectedProvider === config?.provider &&
    !fetchedModels.includes(currentModelVersion)
      ? [currentModelVersion, ...fetchedModels]
      : fetchedModels;

  const providerLabel =
    selectedProvider && !providerInfo && !providersLoading
      ? `${selectedProvider} (not available)`
      : null;

  return (
    <Modal
      title={
        <span>
          Edit Configuration —{" "}
          <Text type="secondary" style={{ fontSize: 14 }}>
            {formatLabel(config?.feature_name)}
          </Text>
        </span>
      }
      open={visible}
      onCancel={onClose}
      afterClose={handleAfterClose}
      footer={null}
      destroyOnClose
      width={600}
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item label="Provider" name="provider" rules={[{ required: true, message: "Provider is required" }]}>
          <Select placeholder="Select provider" onChange={handleProviderChange} loading={providersLoading}>
            {providerLabel && (
              <Option key={selectedProvider} value={selectedProvider}>{providerLabel}</Option>
            )}
            {providerEntries.map(([value, info]) => (
              <Option key={value} value={value}>{info?.name || value}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Model Version" name="model_version" rules={[{ required: true, message: "Model version is required" }]}>
          <Select placeholder={selectedProvider ? "Select model" : "Select a provider first"} disabled={!selectedProvider}>
            {modelOptions.map((m) => (
              <Option key={m} value={m}>{m}</Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Temperature" name="temperature" initialValue={0}>
              <InputNumber min={0} max={2} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Max Tokens" name="max_tokens" initialValue={0}>
              <InputNumber min={0} max={128000} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Top P" name="top_p" initialValue={0}>
              <InputNumber min={0} max={1} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Frequency Penalty" name="frequency_penalty" initialValue={0}>
              <InputNumber min={-2} max={2} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Presence Penalty" name="presence_penalty" initialValue={0}>
              <InputNumber min={-2} max={2} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <PromptFields showNoStoredPrompt={!hasStoredPrompt(config)} />

        <Form.Item label="Enabled" name="enabled" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Save Changes
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditAiConfigModal;
